import React, { useState } from 'react';
import { FiChevronDown, FiChevronRight } from 'react-icons/fi';
import { IconType } from 'react-icons';
import IconWrapper from './IconWrapper';

interface CollapsibleSectionProps {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
  icon?: IconType;
  className?: string;
  headerClassName?: string;
  contentClassName?: string;
  badge?: string | number;
  badgeColor?: 'primary' | 'warning' | 'danger' | 'success' | 'info';
  summary?: React.ReactNode;
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  defaultOpen = false,
  children,
  icon,
  className = '',
  headerClassName = '',
  contentClassName = '',
  badge,
  badgeColor = 'primary',
  summary
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const toggleOpen = () => {
    setIsOpen(!isOpen);
  };

  const getBadgeColorClass = () => {
    switch (badgeColor) {
      case 'warning': return 'bg-amber-100 text-amber-800';
      case 'danger': return 'bg-red-100 text-red-800';
      case 'success': return 'bg-green-100 text-green-800';
      case 'info': return 'bg-blue-100 text-blue-800';
      default: return 'bg-primary/10 text-primary';
    }
  };

  return (
    <div className={`mb-4 border border-gray-200 rounded-lg ${className}`}>
      <div
        className={`p-3 flex items-center cursor-pointer hover:bg-gray-50 ${headerClassName}`}
        onClick={toggleOpen}
      >
        <div className="mr-2 text-primary">
          <IconWrapper icon={isOpen ? FiChevronDown : FiChevronRight} size={18} />
        </div>
        
        {icon && (
          <div className="mr-2 text-primary">
            <IconWrapper icon={icon} size={18} />
          </div>
        )}
        
        <h3 className="font-medium flex-1">{title}</h3>
        
        {badge && (
          <span className={`px-2 py-0.5 text-xs rounded-full ${getBadgeColorClass()} mr-2`}>
            {badge}
          </span>
        )}
        
        {!isOpen && summary && (
          <div className="text-sm text-gray-500 truncate mr-4 max-w-sm">{summary}</div>
        )}
      </div>
      
      {isOpen && (
        <div className={`p-4 pt-0 border-t border-gray-200 ${contentClassName}`}>
          {children}
        </div>
      )}
    </div>
  );
};

export default CollapsibleSection;